import Axios, { AxiosRequestConfig, AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { useAuthStore } from '@store/useAuthStore';
import { downloadFile } from '@shared/utils/downloadFile';
import { refreshAccessToken } from './tokenRefresh';

// 에러 응답 타입
export type ErrorResponse = {
	message?: string;
	statusCode?: number;
};

// API 기본 URL 설정
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:7070';

// Axios 인스턴스 생성
export const AXIOS_INSTANCE = Axios.create({
	baseURL: API_BASE_URL,
	timeout: 60000, // 파일 업로드를 위해 60초로 증가
	headers: {
		'Content-Type': 'application/json'
	},
	withCredentials: true
});

// Request Interceptor
AXIOS_INSTANCE.interceptors.request.use(
	config => {
		const { accessToken } = useAuthStore.getState();

		if (accessToken && config.headers) {
			config.headers.Authorization = `Bearer ${accessToken}`;
		}

		// FormData 를 전송하는 경우 Content-Type 을 삭제
		if (config.data instanceof FormData && config.headers) {
			delete config.headers['Content-Type'];
		}

		// 엑셀 다운로드 엔드포인트는 responseType을 blob으로 설정
		if (config.url?.includes('/excel') || config.url?.includes('/onsite/template')) {
			config.responseType = 'blob';
		}

		// 행이 많은 엑셀 업로드 API는 처리 시간이 길어질 수 있어 타임아웃을 넉넉히 설정
		if (config.url?.includes('/onsite/upload') || config.url?.includes('/bib-numbers/upload')) {
			config.timeout = 300000; // 5분
		}

		return config;
	},
	error => Promise.reject(error)
);

// 토큰 갱신이 필요 없는 API 경로
const AUTH_WHITELIST = ['/admins/login', '/admins/register', '/admins/check-email', '/admins/refresh'];

// 중복 로그인 감지 키워드
const DUPLICATE_LOGIN_KEYWORD = '다른 기기에서 로그인';

// 중복 로그인 에러 여부 확인
const isDuplicateLoginError = (error: AxiosError): boolean => {
	const errorData = error.response?.data as ErrorResponse;
	return errorData?.message?.includes(DUPLICATE_LOGIN_KEYWORD) ?? false;
};

// 중복 로그인 처리 (로그아웃 + alert + 리다이렉트)
const handleDuplicateLogin = (): void => {
	useAuthStore.getState().logout();
	alert('다른 기기에서 로그인되어 현재 세션이 만료되었습니다.\n다시 로그인해주세요.');

	if (typeof window !== 'undefined') {
		window.location.href = '/login';
	}
};

// Response Interceptor
AXIOS_INSTANCE.interceptors.response.use(
	response => {
		// Blob 응답 다운로드
		if (response.data instanceof Blob) {
			downloadFile(response.data, response.headers['content-disposition']);
			toast.info('파일 다운로드가 완료되었습니다.');
		}
		return response;
	},
	async (error: AxiosError) => {
		const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
		const requestUrl = originalRequest.url || '';

		// 취소된 요청은 에러 메시지 표시하지 않음 (정상적인 동작)
		if (Axios.isCancel(error)) return Promise.reject(error);

		// 인증 불필요 API 401 처리 스킵
		const isWhitelisted = AUTH_WHITELIST.some(path => requestUrl.includes(path));

		// 401 에러 & 재시도하지 않은 요청 & whitelist 아닌 경우
		if (error.response?.status === 401 && !originalRequest._retry && !isWhitelisted) {
			originalRequest._retry = true;

			try {
				const accessToken = await refreshAccessToken();

				if (originalRequest.headers) {
					originalRequest.headers.Authorization = `Bearer ${accessToken}`;
				}
				return AXIOS_INSTANCE(originalRequest);
			} catch (refreshError) {
				// 토큰 갱신 실패 시 로그아웃 처리 및 로그인 페이지로 리다이렉트
				// 중복 로그인 여부 확인 및 처리
				if (isDuplicateLoginError(refreshError as AxiosError)) {
					handleDuplicateLogin();
				} else {
					// 일반 토큰 갱신 실패
					useAuthStore.getState().logout();
					if (typeof window !== 'undefined') {
						window.location.href = '/login';
					}
				}

				return Promise.reject(refreshError);
			}
		}

		// 에러 메시지 처리
		if (isDuplicateLoginError(error)) {
			// 중복 로그인인 경우 (whitelist API 또는 재시도 후 401)
			handleDuplicateLogin();
		} else {
			const errorData = error.response?.data as ErrorResponse;
			const errorMessage = errorData?.message || '요청 처리 중 오류가 발생했습니다.';
			toast.error(errorMessage);
			console.error(error);
		}

		return Promise.reject(error);
	}
);

// Orval custom instance
export const axiosInstance = <T>(config: AxiosRequestConfig): Promise<T> => {
	const source = Axios.CancelToken.source();
	const promise = AXIOS_INSTANCE({
		...config,
		cancelToken: source.token
	}).then(({ data }) => data);

	// @ts-ignore
	promise.cancel = () => {
		source.cancel('Query was cancelled');
	};

	return promise;
};
